import { useCallback, useEffect, useRef, useState } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { traverse } from '@atlaskit/adf-utils/traverse/main';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { sanitizeNode } from '@atlaskit/editor-json-transformer/sanitize/sanitize-node';
import type { JSONNode } from '@atlaskit/editor-json-transformer/types';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { useSubscribe } from '@atlaskit/rovo-triggers/main';
import type { TaskAskQuestionConfirmedPayload } from '@atlaskit/rovo-triggers/types';
import { isAIFCAction } from '@atlassian/conversation-assistant-action-helpers/utils';
import {
	CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY,
	CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY,
	isRemixGeneratedFlow,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { trackRemixCreateOptionSelected } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-create-collect';
import { getRemixFeatureArea } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-feature-area';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import type {
	StreamActionClassification,
	StreamActionPart,
	StreamActionPrepare,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { useChatContextEditorContext } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import type { StoreError } from '@atlassian/conversation-assistant-store/controllers/chat-signals';
import {
	CHAT_STORE_STATUS,
	isStoreAssistant,
	isStoreError,
	isStoreHuman,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import type {
	StoreAssistant,
	StoreChatItem,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useChatMessages } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';
import { useStagingAreaActions } from '@atlassian/conversation-assistant-store/controllers/staging-area';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';
import { useConversationById } from '@atlassian/conversation-assistant/controllers/chat-store';
import {
	findLastAssistantAction,
	lastAssistantHasAskQuestionAction,
} from '@atlassian/conversation-assistant/controllers/chat-store/utils';
import { useSmartCreationChatStoreApi } from '@atlassian/conversation-assistant/controllers/smart-creation-api';

import { hasExplicitAbortInLatestTurn } from '../../state/machine/deriveRemixPhase.utils';
import { isRemixRestrictedNodeType } from '../remix/remix-config';
import {
	getSubTypeDisplayName,
	getTypeDisplayName,
	type RemixOptionSelectionParameters,
} from '../remix/remix-overlay';
import { useEditorRemixContext } from './context';
import { getCustomPageEditRequestParams } from './getCustomPageEditRequestParams';
import { getUnsupportedRemixNodeTypes } from './getUnsupportedRemixNodeTypes';
import type {
	DynamicUiSource,
	OpenRemixPreviewOptions,
	RemixProductConfig,
	RemixProductIntegration,
} from './types';
import {
	getContentClassificationFromActionClassification,
	getFinalAIFCMessage,
	withWhiteboardCreateActionData,
} from './utils/message';
import { getFinalMessageId } from './utils/remix-ids';

/**
 * Filters an ADF fragment (array of JSONNode) by recursively removing nodes
 * whose type is in the Remix deny list at every level of the tree.
 * Returns null when given null.
 *
 * Uses the adf-utils `traverse` helper which drops nodes when a visitor
 * returns `false` and automatically rebuilds content arrays.
 */
/**
 * Extracts frame titles from a multiBodiedExtension's parameters.
 *
 * Searches for any array property in `parameters` whose length matches the
 * frame count and whose items have a `title` field. This avoids hard-coding
 * macro-specific parameter names (e.g. `tabs`) so it scales to any MBE
 * variant that stores per-frame titles in its parameters.
 */
const extractFrameTitles = (
	node: JSONNode,
	frameCount: number,
): Array<string | undefined> | undefined => {
	const attrs = node.attrs as Record<string, unknown> | undefined;
	const parameters = attrs?.parameters as Record<string, unknown> | undefined;
	if (!parameters) {
		return undefined;
	}
	for (const value of Object.values(parameters)) {
		if (
			Array.isArray(value) &&
			value.length === frameCount &&
			value.some((item) => typeof item === 'object' && item !== null && 'title' in item)
		) {
			return value.map((item: { title?: string }) => item.title);
		}
	}
	return undefined;
};

/**
 * Unwraps a multiBodiedExtension node, extracting inner content from each
 * extensionFrame so the AI receives clean ADF (paragraphs, lists, etc.)
 * instead of extension wrapper types it cannot process.
 *
 * When frame titles are available in the node's parameters, a heading is
 * injected before each frame's content so the AI preserves the logical
 * grouping rather than treating everything as one flat list.
 */
const unwrapMultiBodiedExtension = (
	node: JSONNode,
	result: JSONNode[],
	isNodeUnsupported: IsNodeUnsupportedForRemix,
): void => {
	const frames = (node.content ?? []).filter(
		(frame): frame is JSONNode => frame?.type === 'extensionFrame' && Array.isArray(frame?.content),
	);
	const frameTitles = extractFrameTitles(node, frames.length);

	frames.forEach((frame, index) => {
		const title = frameTitles?.[index];
		if (title) {
			result.push({
				type: 'heading',
				attrs: { level: 2 },
				content: [{ type: 'text', text: title }],
			});
		}

		for (const child of frame.content!) {
			const filtered = traverse(child as ADFEntity, {
				any: (adfNode) => {
					if (isNodeUnsupported(adfNode)) {
						return false;
					}
				},
			});
			if (filtered !== false) {
				result.push(filtered as JSONNode);
			}
		}
	});
};

/**
 * Product-owned rule for which ADF nodes Remix cannot handle. Matches the shape of
 * `RemixProductConfig.isNodeUnsupportedForRemix` so a product config can be passed straight
 * through. Defaults to the shared deny list.
 */
type IsNodeUnsupportedForRemix = (node: ADFEntity) => boolean;

const defaultIsNodeUnsupportedForRemix: IsNodeUnsupportedForRemix = (node) =>
	isRemixRestrictedNodeType(node.type);

export const filterRemixFragmentNodes = (
	nodes: JSONNode[] | null,
	isNodeUnsupported: IsNodeUnsupportedForRemix = defaultIsNodeUnsupportedForRemix,
): JSONNode[] | null => {
	if (nodes === null) {
		return null;
	}

	const result: JSONNode[] = [];
	for (const node of nodes) {
		if (node.type === 'multiBodiedExtension' && Array.isArray(node.content)) {
			unwrapMultiBodiedExtension(node, result, isNodeUnsupported);
			continue;
		}

		const filtered = traverse(node as ADFEntity, {
			any: (adfNode) => {
				if (isNodeUnsupported(adfNode)) {
					return false;
				}
			},
		});
		if (filtered !== false) {
			result.push(filtered as JSONNode);
		}
	}
	return result;
};

/**
 * Converts editor selection JSON to canonical ADF before applying Remix's existing node filter.
 * The control path deliberately preserves the previous filter-only behavior.
 */
export const prepareRemixFragmentNodes = (
	nodes: JSONNode[] | null,
	isNodeUnsupported?: IsNodeUnsupportedForRemix,
): JSONNode[] | null => {
	if (isExperimentEnabled('cc_maui_polish_changes_batch_6')) {
		if (nodes === null) {
			return null;
		}

		const sanitizedNodes = nodes.flatMap((node) => {
			// The sanitizer can remove an invalid root node at runtime even though its legacy return
			// type only exposes JSONNode.
			const sanitizedNode = sanitizeNode(node, { keepNestedTables: true }) as JSONNode | false;
			return sanitizedNode === false ? [] : [sanitizedNode];
		});

		return filterRemixFragmentNodes(sanitizedNodes, isNodeUnsupported);
	}

	return filterRemixFragmentNodes(nodes, isNodeUnsupported);
};

const isActionComplete = (chatMessages: StoreChatItem[], scopeToLatestTurn?: boolean): boolean => {
	// Scan backwards to find completion signals in the latest turn.
	// If we encounter a HUMAN message first, that marks a turn boundary —
	// any completion signal before it belongs to a previous turn.
	let hasActionCompleteStep = false;
	let hasPlannedAIFCAction = false;
	for (let i = chatMessages.length - 1; i >= 0; i--) {
		const message = chatMessages[i];

		if (message.type === 'ACTION_COMPLETE') {
			hasActionCompleteStep = true;
			break;
		}

		if (scopeToLatestTurn && isStoreAssistant(message)) {
			const actions = message.content?.message?.message?.actions ?? [];
			if (actions.some((action) => isAIFCAction(action) && action.status === 'planned')) {
				hasPlannedAIFCAction = true;
				break;
			}
		}

		if (isStoreHuman(message)) {
			// Stop searching when we reach the start of the most recent turn.
			break;
		}
	}

	if (!scopeToLatestTurn) {
		const lastPlannedAIFCAction = findLastAssistantAction(
			chatMessages,
			(action) => isAIFCAction(action) && action.status === 'planned',
		);
		return hasActionCompleteStep || lastPlannedAIFCAction !== undefined;
	}

	return hasActionCompleteStep || hasPlannedAIFCAction;
};

const isWhiteboardCreateOrUpdateKey = (key: string): boolean =>
	key === CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY || key === CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY;

const shouldUseEphemeralPreviewForAction = (action: { key: string; data?: unknown }): boolean =>
	isRemixGeneratedFlow(action);

// When free-text remix produces a whiteboard (isRemixGenerated=true, no dynamicUiSubtype),
// open the full AIFC staging modal rather than inserting a URL into the page.
const shouldUseAifcStagingAreaForAction = (action: { key: string; data?: unknown }): boolean => {
	if (!isRemixGeneratedFlow(action)) {
		return false;
	}
	return (
		isWhiteboardCreateOrUpdateKey(action.key) &&
		!(action.data as { dynamicUiSubtype?: string } | undefined)?.dynamicUiSubtype
	);
};

const defaultShouldUseEphemeralPreview = (chatMessages: StoreChatItem[]): boolean => {
	for (let i = chatMessages.length - 1; i >= 0; i--) {
		const message = chatMessages[i];
		if (message.type === 'ACTION_PREPARE') {
			return message.action ? shouldUseEphemeralPreviewForAction(message.action) : false;
		}
	}
	return false;
};

export type RemixActionReadyOptions = {
	shouldOpenRovoChat?: boolean;
};

export const useRemixToAIFC = ({
	config,
	onActionReady,
	productIntegration,
	onStagingAIFCFlowStarted,
	onStagingPreviewOpened,
}: {
	config: RemixProductConfig;
	onActionReady?: (options?: RemixActionReadyOptions) => void;
	productIntegration: RemixProductIntegration;
	/**
	 * Called when a non-remix AIFC object output is handed off to the full Smart Creation
	 * staging modal. The editor Remix UI should clear local overlay/decoration state at
	 * this point, because the rest of the create flow is owned by the staging modal rather
	 * than by the editor-owned Remix insertion flow. This must not stop/cancel thinking,
	 * because the AIFC message stream is still active and needs to continue in the modal.
	 * Legacy-only: fires synchronously with the request and never settles lifecycle ownership.
	 */
	onStagingAIFCFlowStarted?: () => void;
	/** Lifecycle acknowledgement, forwarded as `OpenRemixPreviewOptions.onOpen`. */
	onStagingPreviewOpened?: () => void;
}): {
	closeRemixPreview: () => void;
	isRemixAborted: boolean;
	isRemixError: boolean;
	isRemixSafetyViolation: boolean;
	isWaitingForInput: boolean;
	openRemixPreview: (
		options?: Omit<OpenRemixPreviewOptions, 'isFloating'> & {
			action?: { key: string; data?: unknown };
		},
	) => void;
	remixStoreError: StoreError | null;
	sendAIFCPrompt: ({
		userPrompt,
		promptText,
		dynamicUiSubtype,
		dynamicUiType,
		dynamicUiSource,
		interactionSource,
		aspectRatio,
		aiFeatureName,
		isMediaInsertFlow,
		conversationId,
		lifecycleConversationId,
	}: {
		aspectRatio?: string | null;
		dynamicUiSource: DynamicUiSource | undefined;
		dynamicUiSubtype?: string;
		dynamicUiType: RemixOptionSelectionParameters['type'];
		interactionSource?: import('@atlassian/conversation-assistant-instrumentation/constants/analytics').InteractionSource;
		promptText?: string;
		userPrompt?: string;
		aiFeatureName?: string;
		isMediaInsertFlow?: boolean;
		conversationId?: string;
		lifecycleConversationId?: string;
	}) => Promise<string>;
	stopRemixing: () => void;
} => {
	const {
		isUpdate,
		conversationId,
		isEditorRemixStateRearchitectureTreatment,
		setConversationId,
		setStreamingAttemptId,
		setFinalMessage,
		setContentClassification,
		remixUpdateSentinelRef,
		dynamicUiType,
		smartCreationContext,
	} = useEditorRemixContext();
	const experienceTracker = useAIMateExperienceTracker();
	const {
		sendMessage,
		createConversationOptimistically,
		abortRequests,
		activeConversation,
		setSelectedConversation,
	} = useSmartCreationChatStoreApi();
	const {
		setPrompt,
		setDynamicUiContext,
		setUserSelectedDynamicUiSubtype,
		setForceSkipAutoConversationSelection,
	} = useStagingAreaActions();
	const { preferences } = useUserPreferences();
	const [editorContext] = useChatContextEditorContext();
	const editorContextRef = useRef(editorContext);
	editorContextRef.current = editorContext;
	const hasEditablePageContext = editorContext !== undefined && editorContext.isViewMode !== true;
	// remove local state when cleanup platform_editor_ai_remix_in_editor_loading
	const [remixConversationId, setRemixConversationId] = useState<string | undefined>(undefined);

	const activeRemixConversation =
		activeConversation?.id && activeConversation.id === remixConversationId
			? activeConversation
			: null;
	const remixConversationById = useConversationById({
		conversationId: conversationId ?? '',
	});
	const remixConversation = fg('platform_editor_ai_remix_in_editor_loading')
		? remixConversationById
		: activeRemixConversation;
	const remixConversationIdToRead = fg('platform_editor_ai_remix_in_editor_loading')
		? conversationId
		: activeRemixConversation?.id;

	const [chatMessages] = useChatMessages({ conversationId: remixConversationIdToRead ?? '' });
	const isDisambiguationEnabled = fg('platform_editor_ai_remix_disambiguation');
	const [confirmedAskQuestion, setConfirmedAskQuestion] = useState<{
		conversationId: string;
		invocationId: string;
	}>();
	useSubscribe({ topic: 'ai-mate-chat-input-overlay' }, (payload) => {
		if (!isDisambiguationEnabled || payload.type !== 'task-ask-question-confirmed') {
			return;
		}
		const { conversationId: confirmedConversationId, invocationId } = (
			payload as TaskAskQuestionConfirmedPayload
		).data;
		if (confirmedConversationId === remixConversationIdToRead) {
			setConfirmedAskQuestion({ conversationId: confirmedConversationId, invocationId });
		}
	});
	const isCustomOnlyPromptRef = useRef(false);
	let finalMessage = fg('platform_editor_ai_remix_in_editor_loading')
		? getFinalAIFCMessage(chatMessages)
		: undefined;

	// If it's an update, suppress the previously-displayed result until the new
	// variant response arrives. The sentinel is the id of the final message that
	// was on the page when the update was initiated, captured live in
	// useUpdateRemix — stored remix-history ids can be stale (e.g. a BE-driven Ask
	// Rovo edit inherits the prior entry's id rather than its own message id).
	const currentFinalMessageId = getFinalMessageId(finalMessage);
	const previousFinalMessageId = remixUpdateSentinelRef.current;
	if (
		isUpdate &&
		previousFinalMessageId &&
		previousFinalMessageId === currentFinalMessageId &&
		fg('platform_editor_ai_remix_toolbar')
	) {
		finalMessage = undefined;
	}

	const remixStoreError =
		chatMessages.find((message): message is StoreError => isStoreError(message)) ?? null;
	const isRemixError =
		remixConversation?.status === CHAT_STORE_STATUS.deactivatedAgentError ||
		remixStoreError !== null;
	const hasAssistantResponse = chatMessages.some(isStoreAssistant);
	const isCustomOnlyRovoChatEnabled = isExperimentEnabled('cc_maui_polish_changes_batch_5');
	const isCustomOnlyPromptComplete =
		isCustomOnlyRovoChatEnabled &&
		isCustomOnlyPromptRef.current &&
		remixConversation?.status === CHAT_STORE_STATUS.idle &&
		hasAssistantResponse;
	const hasAskQuestionAction =
		isDisambiguationEnabled && lastAssistantHasAskQuestionAction(chatMessages);
	const askQuestionAction = hasAskQuestionAction
		? findLastAssistantAction(chatMessages, (action) => action.key === 'ask_question')
		: undefined;
	const hasPendingAskQuestionAction =
		askQuestionAction !== undefined &&
		askQuestionAction.status !== 'finished' &&
		askQuestionAction.status !== 'cancelled' &&
		askQuestionAction.status !== 'errored';
	const hasConfirmedCurrentQuestion =
		confirmedAskQuestion?.conversationId === remixConversationIdToRead &&
		confirmedAskQuestion?.invocationId === askQuestionAction?.invocationId;
	const isWaitingForInput = hasPendingAskQuestionAction && !hasConfirmedCurrentQuestion;
	const { onRemixInputRequired } = productIntegration;
	const hasNotifiedRemixInputRequiredRef = useRef(false);
	useEffect(() => {
		if (!isWaitingForInput) {
			hasNotifiedRemixInputRequiredRef.current = false;
			return;
		}
		if (!onRemixInputRequired || hasNotifiedRemixInputRequiredRef.current) {
			return;
		}

		hasNotifiedRemixInputRequiredRef.current = true;
		onRemixInputRequired(remixConversationIdToRead);
	}, [isWaitingForInput, onRemixInputRequired, remixConversationIdToRead]);
	const hasExplicitUpdateAbort = isUpdate && hasExplicitAbortInLatestTurn(chatMessages);
	const isLatestTurnActionComplete =
		!hasExplicitUpdateAbort &&
		isActionComplete(chatMessages, isUpdate || isEditorRemixStateRearchitectureTreatment);
	const isRemixAborted =
		!isRemixError &&
		(hasExplicitUpdateAbort ||
			(!isCustomOnlyPromptComplete &&
				!hasPendingAskQuestionAction &&
				remixConversation?.status === CHAT_STORE_STATUS.idle &&
				!isLatestTurnActionComplete &&
				hasAssistantResponse));
	// Set on a (non-fatal) safety/AUP violation. It looks like a completed
	// action here, so isRemixError/isRemixAborted miss it — this flag lets the component
	// dismiss the thinking bar and open the Rovo side chat.
	const [isRemixSafetyViolation, setIsRemixSafetyViolation] = useState(false);

	const actionReadyFired = useRef(false);
	const hasOpenedFullStagingModalRef = useRef(false);
	const recordedFinalMessageId = useRef<string | undefined>(undefined);
	const recordedFinalMessage = useRef<StoreAssistant | undefined>(undefined);
	const previousConversationId = useRef(remixConversationIdToRead);

	useEffect(() => {
		if (previousConversationId.current !== remixConversationIdToRead) {
			actionReadyFired.current = false;
			hasOpenedFullStagingModalRef.current = false;
			recordedFinalMessageId.current = undefined;
			recordedFinalMessage.current = undefined;
			previousConversationId.current = remixConversationIdToRead;
			setIsRemixSafetyViolation(false);
			if (fg('platform_editor_ai_remix_in_editor_loading')) {
				setFinalMessage(undefined);
			}
		}
	}, [remixConversationIdToRead, setFinalMessage]);

	useEffect(() => {
		if (actionReadyFired.current || !onActionReady) {
			return;
		}

		const isConfiguredActionReady = config.isActionReady?.(chatMessages);
		const effectiveActionReady =
			!hasExplicitUpdateAbort && (isConfiguredActionReady ?? isLatestTurnActionComplete);
		const shouldOpenRovoChat = isCustomOnlyPromptComplete && !effectiveActionReady;
		const isActionReady = shouldOpenRovoChat || effectiveActionReady;

		if (isActionReady) {
			actionReadyFired.current = true;
			onActionReady({ shouldOpenRovoChat });
		}
	}, [
		config,
		chatMessages,
		isCustomOnlyPromptComplete,
		hasExplicitUpdateAbort,
		isLatestTurnActionComplete,
		onActionReady,
		remixConversation?.status,
		remixConversationIdToRead,
	]);

	useEffect(() => {
		const finalMessageId = getFinalMessageId(finalMessage);
		const hasRecordedFinalMessage =
			finalMessageId !== undefined
				? recordedFinalMessageId.current === finalMessageId
				: recordedFinalMessage.current === finalMessage;
		if (
			finalMessage &&
			!hasRecordedFinalMessage &&
			!hasOpenedFullStagingModalRef.current &&
			fg('platform_editor_ai_remix_in_editor_loading')
		) {
			recordedFinalMessageId.current = finalMessageId;
			recordedFinalMessage.current = finalMessage;
			setFinalMessage(finalMessage);
			const featureArea = getRemixFeatureArea({ remixType: dynamicUiType });
			experienceTracker.succeed({
				name: ExperienceName.REMIX_START,
				attributes: {
					...(featureArea !== undefined && { featureArea }),
				},
			});
		}
	}, [finalMessage, setFinalMessage, experienceTracker, dynamicUiType]);

	const stopRemixing = useCallback(() => {
		abortRequests?.({ conversationId: remixConversationIdToRead });
	}, [abortRequests, remixConversationIdToRead]);

	// Despite the name, this opens the appropriate AIFC surface for the generated output:
	// remix-generated outputs use the floating ephemeral preview, while non-remix AIFC
	// object outputs (page, database, whiteboard, slides, etc.) use the full staging modal.
	const openRemixPreview = useCallback(
		(
			options?: Omit<OpenRemixPreviewOptions, 'isFloating'> & {
				action?: { key: string; data?: unknown };
			},
		) => {
			setForceSkipAutoConversationSelection(true);
			let shouldUseEphemeralPreview: boolean;
			if (options?.action) {
				shouldUseEphemeralPreview =
					!shouldUseAifcStagingAreaForAction(options.action) &&
					shouldUseEphemeralPreviewForAction(options.action);
			} else {
				shouldUseEphemeralPreview =
					config.shouldUseEphemeralPreview?.(chatMessages) ??
					defaultShouldUseEphemeralPreview(chatMessages);
			}

			productIntegration.openRemixPreview({
				isFloating: shouldUseEphemeralPreview,
				remixConversationId: options?.remixConversationId ?? remixConversationIdToRead,
				...(options?.onOpen ? { onOpen: options.onOpen } : {}),
				...(options?.onSubmit ? { onSubmit: options.onSubmit } : {}),
				// Forward the capability object untouched so a future member cannot be dropped here.
				...(options?.editorInsertionAcknowledgement
					? { editorInsertionAcknowledgement: options.editorInsertionAcknowledgement }
					: {}),
				...(options?.onDismiss ? { onDismiss: options.onDismiss } : {}),
			});
		},
		[
			config,
			chatMessages,
			productIntegration,
			remixConversationIdToRead,
			setForceSkipAutoConversationSelection,
		],
	);

	const sendAIFCPrompt = useCallback(
		async ({
			userPrompt,
			promptText: promptTextOverride,
			dynamicUiSubtype,
			dynamicUiType,
			dynamicUiSource,
			interactionSource,
			aspectRatio,
			aiFeatureName,
			isMediaInsertFlow,
			conversationId: reuseConversationId,
			lifecycleConversationId,
		}: {
			aspectRatio?: string | null;
			dynamicUiSource: DynamicUiSource | undefined;
			dynamicUiSubtype?: string;
			dynamicUiType: RemixOptionSelectionParameters['type'];
			interactionSource?: import('@atlassian/conversation-assistant-instrumentation/constants/analytics').InteractionSource;
			promptText?: string;
			userPrompt?: string;
			aiFeatureName?: string;
			isMediaInsertFlow?: boolean;
			conversationId?: string;
			lifecycleConversationId?: string;
		}) => {
			isCustomOnlyPromptRef.current =
				isCustomOnlyRovoChatEnabled &&
				dynamicUiType === 'custom' &&
				!dynamicUiSubtype &&
				!!userPrompt?.trim();
			const defaultPromptText = 'Remix the selected content';
			const selectedDisplayName = dynamicUiSubtype
				? getSubTypeDisplayName(dynamicUiSubtype)
				: getTypeDisplayName(dynamicUiType);
			const autoText = `Remix the selected content as a ${selectedDisplayName}`;
			const promptText = promptTextOverride
				? promptTextOverride
				: dynamicUiType === 'custom' ||
					  (dynamicUiType === 'image' && userPrompt && isMediaInsertFlow)
					? (userPrompt ?? defaultPromptText)
					: userPrompt
						? `${autoText}. ${userPrompt}`
						: autoText;

			const prompt: DocNode = {
				type: 'doc',
				version: 1,
				content: [
					{
						type: 'paragraph',
						content: [
							{
								type: 'text',
								text: promptText,
							},
						],
					},
				],
			};

			const newConversationId =
				reuseConversationId ?? lifecycleConversationId ?? crypto.randomUUID();
			const streamingAttemptId = crypto.randomUUID();
			setRemixConversationId(newConversationId);
			if (fg('platform_editor_ai_remix_in_editor_loading')) {
				setStreamingAttemptId(streamingAttemptId);
				setConversationId(newConversationId);
				setFinalMessage(undefined);
				setContentClassification(undefined);
			}
			actionReadyFired.current = false;

			if (!reuseConversationId) {
				await createConversationOptimistically({ id: newConversationId });
			} else if (
				UNSAFE_expValNoExposure('cc-ce-maui-remix-menu-multivariant', 'cohort', 'control') ===
				'variant2'
			) {
				setSelectedConversation({ conversationId: reuseConversationId });
			}

			trackRemixCreateOptionSelected({
				experienceTracker,
				conversationId: newConversationId,
				remixType: dynamicUiType,
				remixSubtype: dynamicUiSubtype,
				includesFreegenPrompt: !!userPrompt,
			});

			// 'custom' is a free-form prompt with no block type. Eligible editor page
			// selections map to REMIX_CUSTOM_EDIT; remaining custom requests map to
			// REMIX_CUSTOM. Everything else (chart/diagram/visual-aid/infographic/etc.) is
			// REMIX with the block type as `blocks`. The backend ConfluenceExperienceValidator
			// rejects REMIX without `blocks`, and rejects custom Remix experiences with `blocks`.
			const isCustom = dynamicUiType === 'custom';
			const sourceContentId = smartCreationContext?.confluence?.content?.id;
			const {
				dynamicUiSource: dynamicUiSourceToSend,
				editorContextFallback,
				experienceParams: customPageEditExperienceParams,
			} = getCustomPageEditRequestParams({
				dynamicUiSource,
				dynamicUiType,
				editorContext: editorContextRef.current,
				hasEditablePageContext,
				sourceContentId,
			});
			const creationContextParamsToSend = {
				dynamicUiType,
				dynamicUiSubtype,
				dynamicUiSource: dynamicUiSourceToSend,
				source: 'remix' as const,
				...(customPageEditExperienceParams
					? customPageEditExperienceParams
					: isCustom || dynamicUiType
						? { experience: isCustom ? ('remix_custom' as const) : ('remix' as const) }
						: {}),
				...(!isCustom && dynamicUiType ? { blocks: [dynamicUiType] } : {}),
				...(aspectRatio ? { additionalContext: { aspectRatio } } : {}),
				...(!isCustom && userPrompt ? { hasUserInstruction: true } : {}),
				// The object this remix was initiated from, so the agent session can be anchored to it.
				...(fg('confluence_remix_session_source_content') &&
				productIntegration.sourceContent?.contentId
					? { sourceContent: productIntegration.sourceContent }
					: {}),
			};
			const unsupportedRemixNodeTypes = getUnsupportedRemixNodeTypes(
				dynamicUiSource,
				config.isNodeUnsupportedForRemix,
			);

			sendMessage({
				prompt,
				storeUserMessages: preferences.storeUserMessages,
				conversationId: newConversationId,
				messageSources: {
					interactionSource,
					followUpSource: interactionSource,
					aiFeatureName,
				},
				promptStreamHook: fg('platform_editor_ai_remix_in_editor_loading')
					? {
							// Flag the (non-fatal) safety/AUP violation so the component
							// can dismiss the thinking bar and open the Rovo side chat.
							onSafetyViolation: () => {
								setIsRemixSafetyViolation(true);
							},
							onActionClassification: async (
								actionClassification: StreamActionClassification,
								streamGroupId: string,
							) => {
								const nextClassification = getContentClassificationFromActionClassification(
									actionClassification,
									streamGroupId,
								);
								setContentClassification(nextClassification);
							},
							onActionPart: async (actionPart: StreamActionPart | StreamActionPrepare) => {
								const isWhiteboardCreateAction =
									actionPart.message.key === CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY;

								if (actionPart.type === 'ACTION_PREPARE' && !hasOpenedFullStagingModalRef.current) {
									const useAifcStagingArea = shouldUseAifcStagingAreaForAction(actionPart.message);
									const useEphemeralPreview =
										!useAifcStagingArea && shouldUseEphemeralPreviewForAction(actionPart.message);
									if (!useEphemeralPreview) {
										hasOpenedFullStagingModalRef.current = true;
										openRemixPreview({
											action: actionPart.message,
											remixConversationId: newConversationId,
											onOpen: onStagingPreviewOpened,
										});
										// Legacy cleanup keeps its original timing for every integration.
										onStagingAIFCFlowStarted?.();
									}
								}

								// ACTION_CLASSIFICATION is the semantic source of truth for the stream's
								// content type. ACTION_PREPARE may carry a contentId, but it does not yet
								// represent visible whiteboard output. Wait for the first ACTION_PART before
								// inserting the whiteboard. Native embeds cover the remaining command gap.
								if (
									actionPart.type === 'ACTION_PART' &&
									isWhiteboardCreateAction &&
									!hasOpenedFullStagingModalRef.current
								) {
									setContentClassification((classification) =>
										withWhiteboardCreateActionData(classification, actionPart.message.data),
									);
								}
							},
						}
					: undefined,
				productKey: config.productKey,
				contentContext: 'staging-area',
				unsupportedRemixNodeTypes,
				creationContextParams: creationContextParamsToSend,
				editorContextFallback,
			});

			setPrompt('Remix');
			setDynamicUiContext(dynamicUiType, dynamicUiSubtype, dynamicUiSourceToSend);
			// Preserve the user's original choice separately so EditWithRovoBar can
			// detect when the backend overrides it (setDynamicUiContext above is later
			// overwritten with the backend-produced subtype on response).
			setUserSelectedDynamicUiSubtype(dynamicUiSubtype, newConversationId);

			return newConversationId;
		},
		[
			setConversationId,
			config.productKey,
			createConversationOptimistically,
			setSelectedConversation,
			sendMessage,
			setPrompt,
			setDynamicUiContext,
			setUserSelectedDynamicUiSubtype,
			preferences.storeUserMessages,
			setFinalMessage,
			setContentClassification,
			openRemixPreview,
			onStagingAIFCFlowStarted,
			onStagingPreviewOpened,
			config.isNodeUnsupportedForRemix,
			experienceTracker,
			isCustomOnlyRovoChatEnabled,
			setStreamingAttemptId,
			hasEditablePageContext,
			smartCreationContext?.confluence?.content?.id,
			productIntegration.sourceContent,
		],
	);

	return {
		sendAIFCPrompt,
		stopRemixing,
		isRemixError,
		isRemixAborted,
		isRemixSafetyViolation,
		isWaitingForInput,
		remixStoreError,
		openRemixPreview,
		closeRemixPreview: productIntegration.closeRemixPreview,
	};
};
